import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Copy, Send, Link as LinkIcon, Loader2, CheckCircle2, RefreshCw,
  Sparkles, AlertTriangle, ExternalLink, ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import { api, BACKEND_ORIGIN } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { PlatformChip } from "@/components/shared/Chips";

type Tone = "friendly" | "professional" | "enthusiastic";

interface CaptureContext {
  comment_id: string;
  platform: string;
  author_handle?: string | null;
  author_display_name?: string | null;
  text: string;
  post_external_id?: string | null;
  post_url?: string | null;
  account_id?: string | null;
  account_display_name?: string | null;
  account_handle?: string | null;
  capture_url: string;
  can_send_direct: boolean;
  missing_scope?: string | null;
}

interface GenerateResp {
  message: string;
  language?: string | null;
  engine: string;
  tone: string;
  platform: string;
  can_send_direct: boolean;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** REQUIRED — the source comment_id (from Comment / Lead / Complaint). */
  commentId?: string;
  /** Optional callback for bulk mode (when there is no single comment context). */
  onApplyBulkReply?: (text: string) => void;
  /** Optional title override. */
  title?: string;
}

const TONES: { id: Tone; label: string; emoji: string }[] = [
  { id: "friendly", label: "Friendly", emoji: "👋" },
  { id: "professional", label: "Professional", emoji: "💼" },
  { id: "enthusiastic", label: "Enthusiastic", emoji: "✨" },
];

const platformLabel: Record<string, string> = {
  youtube: "YouTube", instagram: "Instagram", facebook: "Facebook",
  twitter: "X", linkedin: "LinkedIn", tiktok: "TikTok", threads: "Threads",
};

export const CaptureLinkDialog: React.FC<Props> = ({
  open, onOpenChange, commentId, onApplyBulkReply, title,
}) => {
  const [tone, setTone] = useState<Tone>("friendly");
  const [message, setMessage] = useState("");
  const [edited, setEdited] = useState(false);
  const [sendResult, setSendResult] = useState<{ ok: true; platformId?: string; postUrl?: string } | { ok: false; error: string } | null>(null);

  // 1. Resolve capture context (channel + link) for the source comment.
  const ctx = useQuery<CaptureContext>({
    queryKey: ["capture-context", commentId],
    enabled: !!commentId && open,
    queryFn: async () => {
      const r = await api.get(
        `/comments/${commentId}/capture-context`,
        { params: { backend_origin: BACKEND_ORIGIN } },
      );
      return r.data as CaptureContext;
    },
  });

  const bulkMode = !commentId && !!onApplyBulkReply;

  // 2. AI generation — runs on open and when tone changes.
  const gen = useMutation<GenerateResp, any, { link: string; tone: Tone; force?: boolean }>({
    mutationFn: async ({ link, tone: t }) => {
      const r = await api.post(`/comments/${commentId}/generate-capture-reply`, {
        link, tone: t,
      });
      return r.data as GenerateResp;
    },
    onSuccess: (data) => {
      if (!edited) setMessage(data.message);
    },
  });

  // Auto-fire generation once the context loads.
  useEffect(() => {
    if (!open) return;
    if (bulkMode) {
      // Bulk mode: no per-comment context — user picks a channel and gets a
      // simple templated reply (no AI, since there's no single customer to
      // acknowledge).
      return;
    }
    const link = ctx.data?.capture_url;
    if (link && !gen.data && !gen.isPending) {
      setEdited(false);
      gen.mutate({ link, tone });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ctx.data?.capture_url]);

  // Reset local state whenever the dialog closes.
  useEffect(() => {
    if (!open) {
      setEdited(false);
      setMessage("");
      setSendResult(null);
      setTone("friendly");
    }
  }, [open]);

  const regenerate = (nextTone: Tone) => {
    setTone(nextTone);
    if (bulkMode || !ctx.data?.capture_url) return;
    setEdited(false);
    setMessage("");
    setSendResult(null);
    gen.mutate({ link: ctx.data.capture_url, tone: nextTone, force: true });
  };

  const send = useMutation({
    mutationFn: async () => {
      if (!commentId) throw new Error("no comment");
      const r = await api.post(`/comments/${commentId}/send-to-platform`, {
        reply_text: message,
        confirmed: true,
      });
      return r.data as { reply_platform_id?: string | null };
    },
    onSuccess: (data) => {
      setSendResult({ ok: true, platformId: data.reply_platform_id || undefined, postUrl: ctx.data?.post_url || undefined });
      toast.success("Reply posted on the platform ✓");
    },
    onError: (err: any) => {
      const detail = err?.response?.data?.detail || err?.message || "Send failed";
      setSendResult({ ok: false, error: detail });
      toast.error(detail);
    },
  });

  const copy = async (text: string, msg: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(msg);
    } catch {
      toast.error("Copy failed");
    }
  };

  const platform = ctx.data?.platform || "";
  const platformName = platformLabel[platform] || platform || "the platform";
  const canDirectSend = !!ctx.data?.can_send_direct && !sendResult;

  // ---------- Bulk mode UI ----------
  const bulkTemplate = useMemo(() => {
    // Very minimal in bulk mode — no AI, since one message must fit N comments.
    return "Thank you for your interest! Please submit your details here so our team can contact you:\n{{capture_link}}";
  }, []);

  // ---------- Render ----------
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-2xl" data-testid="capture-link-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[color:var(--sg-violet)]" />
            {title || (bulkMode ? "Send capture link to selected" : "Send AI reply with capture link")}
          </DialogTitle>
          <DialogDescription>
            {bulkMode
              ? "This templated reply will be applied to every selected comment."
              : "AI drafts a warm, personalized reply that acknowledges the customer's question and includes your capture link."}
          </DialogDescription>
        </DialogHeader>

        {bulkMode ? (
          <BulkPanel
            template={bulkTemplate}
            onApply={(txt) => { onApplyBulkReply?.(txt); onOpenChange(false); }}
            onClose={() => onOpenChange(false)}
          />
        ) : ctx.isLoading ? (
          <div className="py-10 flex items-center justify-center text-[color:var(--sg-muted)]">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading channel…
          </div>
        ) : ctx.isError || !ctx.data?.account_id || !ctx.data.capture_url ? (
          <div className="p-4 rounded-md bg-amber-50 border border-amber-200 text-sm text-amber-900" data-testid="capture-link-no-channel">
            No connected channel found for this comment's platform. Head to{" "}
            <a href="/platforms" className="underline font-medium">Platforms</a> to connect one.
          </div>
        ) : (
          <div className="space-y-4" data-testid="capture-dialog-body">
            {/* Channel — locked chip */}
            <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="capture-channel-chip">
              <span className="sg-label !mb-0">Replying as</span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 border border-slate-200 px-2.5 py-1 text-slate-700 font-medium">
                <PlatformChip platform={platform} />
                {ctx.data.account_display_name || ctx.data.account_handle || "Channel"}
              </span>
              {ctx.data.post_url && (
                <a
                  href={ctx.data.post_url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="capture-source-post-link"
                  className="inline-flex items-center gap-1 text-[color:var(--sg-primary)] hover:underline"
                >
                  <ExternalLink className="h-3 w-3" /> View source post
                </a>
              )}
            </div>

            {/* Original comment — for context */}
            <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-xs" data-testid="capture-original-comment">
              <div className="text-[color:var(--sg-muted)] mb-1">
                {ctx.data.author_handle || ctx.data.author_display_name || "Customer"} said:
              </div>
              <div className="text-slate-800 line-clamp-3">{ctx.data.text}</div>
            </div>

            {/* Tone selector */}
            <div>
              <div className="sg-label mb-1.5">Tone</div>
              <div className="flex flex-wrap gap-2" data-testid="capture-tone-group">
                {TONES.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => regenerate(t.id)}
                    disabled={gen.isPending}
                    data-testid={`capture-tone-${t.id}`}
                    className={
                      `inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition ` +
                      (tone === t.id
                        ? "bg-[color:var(--sg-violet)] text-white border-transparent shadow-sm"
                        : "bg-white text-slate-700 border-slate-200 hover:border-slate-300 hover:bg-slate-50")
                    }
                  >
                    <span>{t.emoji}</span> {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* AI reply */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <div className="sg-label !mb-0">
                  AI-drafted reply
                  {gen.data?.language && (
                    <span className="ml-2 text-[10px] font-normal text-[color:var(--sg-muted)]">
                      · {gen.data.language}
                    </span>
                  )}
                  {gen.data?.engine === "template" && (
                    <span className="ml-2 text-[10px] font-normal text-amber-600">
                      · templated (upgrade to Pro for AI drafts)
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => regenerate(tone)}
                    disabled={gen.isPending}
                    data-testid="capture-regenerate-btn"
                    className="text-[11px] text-[color:var(--sg-primary)] hover:underline inline-flex items-center gap-1 disabled:opacity-50"
                  >
                    <RefreshCw className={`h-3 w-3 ${gen.isPending ? "animate-spin" : ""}`} />
                    Regenerate
                  </button>
                  <button
                    type="button"
                    onClick={() => copy(message, "Reply copied")}
                    disabled={!message}
                    data-testid="capture-copy-message-btn"
                    className="text-[11px] text-[color:var(--sg-primary)] hover:underline inline-flex items-center gap-1 disabled:opacity-50"
                  >
                    <Copy className="h-3 w-3" /> Copy
                  </button>
                </div>
              </div>
              {gen.isPending && !message ? (
                <div className="h-32 rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center text-xs text-[color:var(--sg-muted)]">
                  <Loader2 className="h-4 w-4 animate-spin mr-2" /> Crafting a warm reply…
                </div>
              ) : (
                <Textarea
                  data-testid="capture-message-text"
                  value={message}
                  onChange={(e) => { setEdited(true); setMessage(e.target.value); }}
                  rows={6}
                  maxLength={2000}
                  className="font-normal"
                />
              )}
              <div className="text-[10px] text-[color:var(--sg-muted)] text-right mt-1">
                {message.length} / 2000
              </div>
            </div>

            {/* Capture link preview */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <div className="sg-label !mb-0">Capture link</div>
                <button
                  type="button"
                  onClick={() => copy(ctx.data!.capture_url, "Link copied")}
                  data-testid="capture-copy-link-btn"
                  className="text-[11px] text-[color:var(--sg-primary)] hover:underline inline-flex items-center gap-1"
                >
                  <Copy className="h-3 w-3" /> Copy link
                </button>
              </div>
              <div
                className="text-[11px] font-mono bg-slate-50 border border-slate-200 rounded-md p-2 break-all text-slate-700"
                data-testid="capture-link-url"
              >
                {ctx.data.capture_url}
              </div>
            </div>

            {/* Missing scope warning (YouTube) */}
            {ctx.data.missing_scope && (
              <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 flex items-start gap-2" data-testid="capture-scope-warning">
                <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5" />
                <div>
                  <b>Reconnect required</b> — this channel's OAuth grant is missing the{" "}
                  <code className="font-mono">{ctx.data.missing_scope}</code> permission needed to post replies.
                  Reconnect on <a href="/platforms" className="underline">Platforms</a> to enable direct sending.
                </div>
              </div>
            )}

            {/* Non-YouTube: coming soon banner */}
            {!ctx.data.can_send_direct && !ctx.data.missing_scope && platform !== "youtube" && (
              <div className="rounded-md bg-sky-50 border border-sky-200 p-3 text-xs text-sky-900 flex items-start gap-2" data-testid="capture-direct-unsupported">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <div>
                  Direct reply posting on <b>{platformName}</b> is coming soon. For now, copy the message and
                  paste it as your reply — captured leads will still be tracked.
                </div>
              </div>
            )}

            {/* Send result — success or retry */}
            {sendResult?.ok && (
              <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800 flex items-center gap-2" data-testid="capture-sent-success">
                <CheckCircle2 className="h-4 w-4" />
                <span className="flex-1">Reply posted on {platformName}.</span>
                {sendResult.postUrl && (
                  <a
                    href={sendResult.postUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-emerald-700 hover:underline text-xs inline-flex items-center gap-1"
                    data-testid="capture-view-on-platform"
                  >
                    View on {platformName} <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            )}
            {sendResult && !sendResult.ok && (
              <div className="rounded-md bg-rose-50 border border-rose-200 p-3 text-sm text-rose-800 flex items-start gap-2" data-testid="capture-sent-error">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="font-medium">Couldn't post the reply.</div>
                  <div className="text-xs mt-0.5 opacity-90">{sendResult.error}</div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-rose-300 text-rose-700 hover:bg-rose-100 hover:text-rose-800"
                  onClick={() => { setSendResult(null); send.mutate(); }}
                  disabled={send.isPending}
                  data-testid="capture-retry-send-btn"
                >
                  <RefreshCw className={`h-3.5 w-3.5 mr-1 ${send.isPending ? "animate-spin" : ""}`} />
                  Retry
                </Button>
              </div>
            )}
          </div>
        )}

        {!bulkMode && (
          <DialogFooter className="gap-2 flex-wrap">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="capture-close-btn">
              Close
            </Button>
            <Button
              variant="outline"
              onClick={() => copy(message, "Reply copied — paste it as your reply")}
              disabled={!message}
              data-testid="capture-copy-and-close-btn"
            >
              <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy reply
            </Button>
            {canDirectSend && (
              <Button
                onClick={() => send.mutate()}
                disabled={!message || send.isPending}
                data-testid="capture-send-direct-btn"
                className="bg-gradient-to-r from-[color:var(--sg-violet)] to-[color:var(--sg-primary)] text-white hover:opacity-90"
              >
                {send.isPending ? (
                  <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Sending…</>
                ) : (
                  <><Send className="h-3.5 w-3.5 mr-1.5" /> Send reply on {platformName}</>
                )}
              </Button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

// -------- Bulk mode subcomponent (unchanged simple flow) --------

const BulkPanel: React.FC<{
  template: string;
  onApply: (text: string) => void;
  onClose: () => void;
}> = ({ template, onApply, onClose }) => {
  const [text, setText] = useState(template);
  return (
    <>
      <div className="text-xs text-[color:var(--sg-muted)] bg-amber-50 border border-amber-200 rounded-md p-3">
        <b>Bulk mode.</b> The token <code className="font-mono">{"{{capture_link}}"}</code> is replaced per comment
        with that comment's own capture URL (channel + ref).
      </div>
      <Textarea
        data-testid="bulk-capture-text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={6}
        maxLength={2000}
      />
      <DialogFooter className="gap-2 flex-wrap">
        <Button variant="outline" onClick={onClose} data-testid="bulk-capture-close-btn">Close</Button>
        <Button
          onClick={() => onApply(text)}
          data-testid="capture-apply-bulk-btn"
          className="bg-gradient-to-r from-[color:var(--sg-violet)] to-[color:var(--sg-primary)] text-white hover:opacity-90"
        >
          <Send className="h-3.5 w-3.5 mr-1.5" /> Apply to selected
        </Button>
      </DialogFooter>
    </>
  );
};

export default CaptureLinkDialog;
